import React, { Component } from "react";
import "./EnterPhoneNumber.css";
import Spinner from "./Spinner";
import Api from "./api";
import InputMask from "react-input-mask";
import ReactMarkdown from "react-markdown";

interface State {
  fields: {
    phoneNumber: string;
    tos: boolean;
    acceptMarketing: boolean;
  };
  error: {
    field?: "tos" | "phoneNumber";
    msg?: string;
  };
  processing: boolean;
}

interface Props {
  onStepComplete: (n: string, m: boolean) => void;
  sendVerifyCode: Api["sendVerifyCode"];
  userId: string;
  phoneNumber: string;
  shopName: string;
  optinSettings: {
    discountText: string;
    discountUrl: string;
    modal: {
      body: string;
      title: string;
    };
    page: {
      body: string;
      title: string;
    };
    termsOfService: string;
  };
  tosUrl: string;
  policyUrl: string;
  color: string;
  lightColor: string;
  optinMethod: string;
}

class EnterPhoneNumber extends Component<Props, State> {
  state = {
    fields: {
      phoneNumber: "",
      tos: false,
      acceptMarketing: false,
    },
    error: {} as State["error"],
    processing: false,
  };

  componentDidMount() {
    const { phoneNumber } = this.props;

    this.setState({
      fields: {
        phoneNumber: phoneNumber,
        tos: false,
        acceptMarketing: false,
      },
    });
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name =
      target.name === "tos" || target.name === "acceptMarketing"
        ? target.name
        : "phoneNumber";
    this.setState((prevState) => {
      return {
        ...prevState,
        fields: {
          ...prevState.fields,
          [name]: value,
        },
      };
    });
  };

  handleNumberSubmit = (e: React.FormEvent) => {
    this.setState({ processing: true, error: {} });
    e.preventDefault();
    const {
      fields: { phoneNumber, tos, acceptMarketing },
    } = this.state;
    const { userId, sendVerifyCode, optinMethod } = this.props;
    const number = phoneNumber.replace(/[^\d]/g, "");
    if (number.length < 10) {
      this.setState({
        error: {
          field: "phoneNumber",
          msg: "Please enter your 10-digit number, with area code.",
        },
        processing: false,
      });
    } else if (number.length > 11) {
      this.setState({
        error: {
          field: "phoneNumber",
          msg: "Not a valid 10-digit number. (Only US numbers.)",
        },
        processing: false,
      });
    } else if (!tos) {
      this.setState({
        error: {
          field: "tos",
          msg: "Please accept the Terms of Service.",
        },
        processing: false,
      });
    } else {
      sendVerifyCode(phoneNumber, userId, optinMethod)
        .then(async (r) => {
          const json = await r.json();

          if (r.status === 200) {
            this.props.onStepComplete(phoneNumber, acceptMarketing);
          } else {
            console.log(`Received ${r.status} from the server.`);
            const { detail, type } = json.error;
            detail && console.log(`Error received: ${detail}`);
            let errorMsg = "Uh oh. Something went wrong. Try one more time?";
            if (type === "user" && detail) {
              errorMsg = detail;
            }
            this.setState({
              processing: false,
              error: { msg: errorMsg },
            });
          }
        })
        .catch(() => {
          this.setState({
            processing: false,
            error: { msg: "Uh oh. Something went wrong. Try one more time?" },
          });
        });
    }
  };

  render() {
    const {
      fields: { phoneNumber, tos, acceptMarketing },
      error,
      processing,
    } = this.state;

    const {
      shopName,
      optinSettings,
      tosUrl,
      policyUrl,
      color,
      lightColor,
    } = this.props;
    const colors = {
      "--primary-color": color,
      "--secondary-color": lightColor,
    } as React.CSSProperties;

    return (
      <div className="EnterPhoneNumber" style={colors}>
        <div className="title-container">
          <div className="PhoneIcon" />
          <h1>{optinSettings && optinSettings.modal.title}</h1>
        </div>
        <div className="Header">
          <div className="Header-desc">
            <h3
              dangerouslySetInnerHTML={{
                __html: optinSettings && optinSettings.modal.body,
              }}
            />
          </div>
        </div>
        <div className="InputArea">
          <div className="Errors">{error.msg}</div>
          <form onSubmit={this.handleNumberSubmit}>
            <div className="TOSArea">
              <div className="TOS-Checkbox">
                <input
                  type="checkbox"
                  className="input-checkbox"
                  checked={tos}
                  onChange={this.handleInputChange}
                  name="tos"
                />
                <span
                  className={
                    `checkmark` + (error.field === "tos" ? " has-error" : "")
                  }
                />
              </div>
              <label className="TOS">
                {optinSettings && optinSettings.termsOfService ? (
                  <ReactMarkdown
                    source={optinSettings.termsOfService}
                    linkTarget={"_"}
                    renderers={{ paragraph: "span" }}
                  />
                ) : (
                  <React.Fragment>
                    By submitting, I agree to receive automated text messages at
                    the phone number provided. For more information, see{" "}
                    {shopName}'s{" "}
                    <a href={tosUrl} target="_blank">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href={policyUrl} target="_blank">
                      Privacy Policy
                    </a>
                    .
                  </React.Fragment>
                )}
              </label>
            </div>
            <div className="PhoneArea">
              <InputMask
                maskChar="_"
                mask="+1 (999) 999-9999"
                className="PhoneNumber"
                placeholder="Enter phone number"
                onChange={this.handleInputChange}
                value={phoneNumber}
              >
                {(
                  inputProps: JSX.IntrinsicAttributes &
                    React.ClassAttributes<HTMLInputElement> &
                    React.InputHTMLAttributes<HTMLInputElement>
                ) => <input type="tel" {...inputProps} />}
              </InputMask>
              <button
                type="submit"
                disabled={processing}
                className={"Submit" + (processing ? " processing" : "")}
              >
                <Spinner style={{ marginLeft: "30px" }} visible={processing} />
                Next Step
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default EnterPhoneNumber;
